import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Box, Text, Button, Flex } from '@radix-ui/themes';
import * as Checkbox from '@radix-ui/react-checkbox';
import * as Form from '@radix-ui/react-form';
import api from '../../api/axios.js';
import { useNavigate, Link } from 'react-router-dom';

const Login = ({ setToken, apiUrl }) => {
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!username.trim()) {
      setError('Please enter your username');
      setLoading(false);
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post(`/login`, { username: username.trim(), password });
      const token = response.data.token;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setToken(token);

      if (response.data.user.is_admin) {
        navigate('/backend/lessons');
      } else {
        navigate('/profile');
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const message = error.response.data?.message;
        
        switch (status) {
          case 401:
            setError('Invalid username or password. Please check your credentials and try again.');
            break;
          case 400:
            setError(message || 'Invalid request. Please check your input.');
            break;
          case 500:
            setError('Server error. Please try again later.');
            break;
          default:
            setError(message || 'Login failed. Please try again.');
        }
      } else if (error.request) {
        // Network error
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Other errors
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex style={{ minHeight: '80vh', justifyContent: 'space-around', alignItems: 'center' }}>
      {/* Left Column - Image */}
      <Box style={{ flex: 1, display: 'block' }} className="lg:block">
        <img
          src="/images/login-left-hug.jpg"
          alt="Login"
          style={{ width: '100%', height: 'auto', objectFit: 'cover', objectPosition: 'center' }}
        />
      </Box>

      {/* Right Column - Login Form */}
      <Flex align="center" justify="center" style={{ flex: 1, padding: '2rem', backgroundColor: '#F36F25', minHeight: '400px' }}>
        <Container size="2">
          <Text size="6" weight="bold" align="center" mb="6"></Text>
          
          <form style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} onSubmit={handleSubmit}>
            {/* Error Display */}
            {error && (
              <Box style={{
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '1rem'
              }}>
                <Text style={{ color: '#dc3545', fontSize: '14px', fontWeight: '500' }}>
                  ❌ {error}
                </Text>
              </Box>
            )}

            <Flex align="center" gap="4">
              <Text as="label" size="3" style={{ width: '100px', color: 'white', fontWeight: 'bold' }}>
                Username
              </Text>
              <input 
                type="text"
                placeholder="Enter your username"
                className="flex-1 px-3 py-2 border rounded"
                style={{ 
                  backgroundColor: 'white', 
                  flex: 2,
                  borderColor: error && !username.trim() ? '#dc3545' : undefined
                }}
                value={username}
                onChange={e => setUserName(e.target.value)}
                disabled={loading}
              />
            </Flex>

            <Flex align="center" gap="4">
              <Text as="label" size="3" style={{ width: '100px', color: 'white', fontWeight: 'bold' }}>
                Password
              </Text>
              <input 
                type="password"
                placeholder="Enter your password"
                className="flex-1 px-3 py-2 border rounded"
                style={{ 
                  backgroundColor: 'white', 
                  flex: 2,
                  borderColor: error && !password.trim() ? '#dc3545' : undefined
                }}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={loading}
              />
            </Flex>

            <Flex align="center" gap="4">
              <Text as="label" size="2" style={{ width: '100px' }}> </Text>
              <Button 
                size="3" 
                variant="solid" 
                style={{ backgroundColor: 'black', color: 'white', flex: 2}}
                disabled={loading}
                type="submit"
              >
                {loading ? 'กำลังเข้าสู่ระบบ...' : 'คลิกเพื่อเข้าสู่ระบบ'}
              </Button>
            </Flex>
            <Flex align="center" gap="4" justify="center">
              <Text as="label" size="2" style={{ width: '100px' }}> </Text>
              <Link to="/register" style={{ color: 'white', textDecoration: 'underline' }}>คลิกที่นี่เพื่อลงทะเบียน</Link>
            </Flex>
          </form>
        </Container>
      </Flex>
    </Flex>
  );
};

export default Login;

Login.propTypes = {
  setToken: PropTypes.func.isRequired
}